import { useState, useRef } from "react"
import styled from "styled-components"
import ReactCrop, { convertToPixelCrop } from "react-image-crop"
import "react-image-crop/dist/ReactCrop.css"
import { HelperText } from "ui/base/helperText/HelperText.jsx"
import { DeleteIcon } from "ui/icons/index.js"
import { ImageIcon } from "ui/icons/inputIcons/ImageIcon.jsx"
import { convertHexToRGBA, defaultThemeColors, themeColor, mediaSizes } from "ui/theme/index.js"
import { Modal } from "ui/base/modal/Modal"
import { setCanvasPreview } from "utils"
import { AppButton } from "../appButton/AppButton"
import {useToast} from "hooks";

const ASPECT_RATIO = 1
const minImages = 2

export const PhotoGalleryUploader = ({ selectedImages, setSelectedImages, handleImageDeselect, formDataErrors }) => {
    const [cropModalOpen, setCropModalOpen] = useState(false)
    const [cropQueue, setCropQueue] = useState([])
    const [currentCropIndex, setCurrentCropIndex] = useState(0)
    const [imgSrc, setImgSrc] = useState("")
    const [crop, setCrop] = useState()
    const imgRef = useRef(null)
    const previewCanvasRef = useRef(null)
    const firstActiveId = selectedImages.find((img) => !img.deleted)?.id

    const onSelectFiles = (e) => {
        const files = Array.from(e.target.files || [])
        if (!files.length) return

        const currentCount = selectedImages.filter((img) => !img.deleted).length
        const availableSlots = 8 - currentCount

        if (availableSlots <= 0) {
            useToast("error", "Max 8 images allowed");
            e.target.value = ""
            return
        }

        const validFiles = files.slice(0, availableSlots)

        if (files.length > availableSlots) {
            useToast("error", `Only first ${availableSlots} images accepted (max 8 allowed).`);
        }

        if (!validFiles.length) return

        e.target.value = ""
        setCropQueue(validFiles)
        setCurrentCropIndex(0)
        loadImage(validFiles[0])
        setCropModalOpen(true)
    }

    const loadImage = (file) => {
        if (!file || !(file instanceof Blob)) return
        const reader = new FileReader()
        reader.onload = () => setImgSrc(reader.result)
        reader.readAsDataURL(file)
    }

    const onImageLoad = (e) => {
        const { width, height } = e.currentTarget
        const size = Math.min(width, height)
        const cropBox = {
            unit: "px",
            width: size,
            height: size,
            x: (width - size) / 2,
            y: (height - size) / 2,
        }
        setCrop(cropBox)
    }

    const handleCropChange = (newCrop, percentCrop) => {
        if (crop) {
            setCrop({
                ...crop,
                x: newCrop.x,
                y: newCrop.y,
            })
        }
    }

    const saveCroppedImage = () => {
        const img = imgRef.current
        const canvas = previewCanvasRef.current
        if (!img || !canvas) return

        setCanvasPreview(img, canvas, convertToPixelCrop(crop, img.width, img.height))

        canvas.toBlob((blob) => {
            if (!blob) return

            const croppedFile = new File([blob], `cropped_${Date.now()}.jpg`, {
                type: "image/jpeg",
            })

            setSelectedImages((prev) => [
                ...prev,
                {
                    id: `${new Date().getTime()}_${prev.length}`,
                    name: "galleryImage",
                    type: croppedFile.type,
                    uri: croppedFile,
                    uploaded: false,
                    deleted: false,
                },
            ])

            const nextIndex = currentCropIndex + 1
            if (nextIndex < cropQueue.length) {
                setCurrentCropIndex(nextIndex)
                loadImage(cropQueue[nextIndex])
            } else {
                setCropModalOpen(false)
                setCropQueue([])
                setCurrentCropIndex(0)
                setImgSrc("")
            }
        }, "image/jpeg")
    }

    return (
        <GalleryContainer>
            <HelperText>Add at least {minImages} different photos that show the class experience</HelperText>

            <GalleryAddButton htmlFor="addImage">
                <GalleryAddButtonTitle>
                    <ImageIcon />
                    <HelperText style={{ pointerEvents: "none" }}>
                        <HelperText color="blue" weight={600}>
                            Upload
                        </HelperText>{" "}
                        Upload your file here
                    </HelperText>
                </GalleryAddButtonTitle>
                <input id="addImage" type="file" accept="image/*" multiple hidden onChange={onSelectFiles} />
            </GalleryAddButton>

            <GalleryImagesContainer>
                {selectedImages.map(
                    (image, index) =>
                        !image.deleted && (
                            <GalleryImageContainer key={image.id || index}>
                                <GalleryImage
                                    src={typeof image.uri === "string" ? image.uri : URL.createObjectURL(image.uri)}
                                    alt="uploaded"
                                />
                                {image.id === firstActiveId && <MainImage>Main Photo</MainImage>}
                                <GalleryDeleteImageContainer onClick={() => handleImageDeselect(image.id)}>
                                    <DeleteIcon size={20} />
                                </GalleryDeleteImageContainer>
                            </GalleryImageContainer>
                        ),
                )}
            </GalleryImagesContainer>

            {formDataErrors?.gallery && (
                <HelperText color="pink" size="extraSmall" textAlign="left">
                    {formDataErrors.gallery}
                </HelperText>
            )}

            <Modal show={cropModalOpen} handleClose={() => setCropModalOpen(false)}>
                {imgSrc && (
                    <CropContainer>
                        <HelperText color="blue" size="small">
                            Cropping image {currentCropIndex + 1} of {cropQueue.length}
                        </HelperText>

                        <ReactCrop
                            crop={crop}
                            keepSelection
                            ruleOfThirds={false}
                            aspect={ASPECT_RATIO}
                            circularCrop={false}
                            onChange={handleCropChange}
                            minWidth={crop?.width}
                            minHeight={crop?.height}
                            maxWidth={crop?.width}
                            maxHeight={crop?.height}
                        >
                            <CropImage ref={imgRef} src={imgSrc} onLoad={onImageLoad} />
                        </ReactCrop>

                        <AppButton onClick={saveCroppedImage}>
                            {currentCropIndex === cropQueue.length - 1 ? "Finish" : "Next"}
                        </AppButton>
                    </CropContainer>
                )}
                <canvas ref={previewCanvasRef} style={{ display: "none" }} />
            </Modal>
        </GalleryContainer>
    )
}

const GalleryContainer = styled.div`
    display: grid;
    gap: 1rem;
`

const GalleryAddButton = styled.label`
    padding: 20px 38px;
    background-color: ${themeColor("border")};
    border: 2px dashed ${convertHexToRGBA(defaultThemeColors.blue, 0.5)};
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 5px;
    cursor: pointer;
    height: 140px;

    @media (min-width: 768px) {
        height: 280px;
    }
`

const GalleryAddButtonTitle = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
`

const GalleryImagesContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;

    @media (min-width: ${mediaSizes.md}) {
        grid-template-columns: repeat(4, 1fr);
    }

    @media (min-width: ${mediaSizes.xl}) {
        grid-template-columns: repeat(8, 1fr);
    }
`

const GalleryImageContainer = styled.div`
    position: relative;
`

const GalleryImage = styled.img`
    width: 100%;
    height: 90px;
    object-fit: cover;
    border-radius: 10px;
    border: 1px solid ${convertHexToRGBA(defaultThemeColors.blue, 0.5)};
`

const GalleryDeleteImageContainer = styled.div`
    position: absolute;
    background-color: ${defaultThemeColors.pink};
    color: ${defaultThemeColors.white};
    right: 0;
    top: 0;
    width: 30px;
    height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-top-right-radius: 10px;
    border-bottom-left-radius: 5px;
    cursor: pointer;
`

const MainImage = styled.div`
    background-color: ${defaultThemeColors.pink};
    color: ${defaultThemeColors.white};
    padding: 0.5rem;
    border-bottom-right-radius: 1rem;
    border-bottom-left-radius: 1rem;
    text-align: center;
`

const CropContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
`

const CropImage = styled.img`
    max-width: 100%;
    max-height: 400px;
    border-radius: 0.5rem;
    object-fit: contain;
`
